/**
 * Firestore-mode MIRROR INTENT (ATL-1459; ATL-1445 stage 3, P7 — plan §4 "ATL-1459", §8 P7).
 *
 * THE LOSS THIS CLOSES. In Firestore mode a writer commits its Firestore batch and only THEN, in the
 * JS continuation, sends the Supabase mirror (`bestEffortDualWrite`). Offline, `batch.commit()`
 * resolves only when the server acknowledges it. If the app is killed or reloaded in between — the
 * till restarted before it reconnected, an OTA reload, ATL-888's live-rollback reload — the native
 * Firestore SDK replays its PERSISTED write on the next launch, but the continuation that would have
 * sent the mirror is gone, and the dual-write outbox (`dualWriteOutbox.ts`) records only failures it
 * OBSERVED. The Firestore document exists and Supabase never hears of it. Measured once in the
 * stage-0 baseline (`IhNM7XZrtJPRhezgdFhb`, 1 of 40 offline sales); it is defect 1 of stage-0 §4.3.
 *
 * THE FIX. The mirror's request is built and persisted BEFORE `batch.commit()`, in the outbox v2
 * storage engine (`global/Write`), so no instant exists at which Firestore holds the write and
 * nothing on the device holds its mirror:
 *
 *     persist(intent) ─► batch.commit() ─► bestEffortDualWrite(...) ─► release(intent)
 *
 * On the happy path the intent is removed once the mirror call has returned — whatever it returned:
 * a success, a failure the v1 outbox queued, a refusal it reported, a gate skip. Every one of those is
 * today's behaviour, and the intent does not change it. The intent only ever matters when the
 * continuation DIES; then it is found by the RECOVERY pass, which runs when the session's organization
 * becomes known (app start) and asks Firestore — the store every device converges on — what became of
 * the commit. The live path mirrors only after `batch.commit()` resolved, i.e. after the SERVER
 * accepted the batch; recovery holds itself to the same condition:
 *
 *  - PRESENT — the document is on the server (a server read, or a cached copy with no pending write,
 *    which is the server's acknowledged version) → the commit was accepted and the mirror did not
 *    provably run: the request is handed to the v1 outbox (`enqueueRecoveredMirrorIntent`) exactly as
 *    the live path would have queued it had it run and failed, and the intent is removed. From there
 *    every v1 rule applies: the gate, per-document order, supersede (which never replaces an ATL-1503
 *    preconditioned entry), backoff, drop rules.
 *  - PENDING — the document is in this device's cache WITH a pending write: the SDK replayed its
 *    persisted batch (the ATL-1459 till, relaunched still offline) and the server has not accepted it
 *    yet → kept, like the live path's `await batch.commit()`, until it is PRESENT.
 *  - ABSENT — the server has no such document, or this device holds a pending local delete of it → the
 *    commit never happened (the app died before it, or the batch was refused) or was undone: the intent
 *    is discarded, since sending it would create a Supabase-only record. Only once this process has
 *    seen it absent twice, `ABSENT_CONFIRM_MS` apart: a commit a dead web tab had already put on the
 *    wire must be able to land first. Each discard is reported (Sentry + `mirror_intent_discarded_absent`).
 *  - UNKNOWN (offline, a read error, not this session's organization) → kept and retried. PENDING and
 *    UNKNOWN are dropped LOUDLY only after `MAX_AGE_MS`, the same bound the v1 outbox gives every entry
 *    (which would expire the recovered entry at once anyway: it keeps the write's own age).
 *
 * NO DOUBLE SEND on the happy path: recovery never touches an intent whose writer is still running
 * (`live` in this JS context; on web, another tab whose owner Web Lock is still held). The one residual
 * is a crash between the mirror LANDING and the intent's removal: the next launch replays it once
 * more, and the backend answers a replayed create from the row it already has (`resolveReplayedCreate`,
 * keyed on the document's firestore_id), so nothing is booked twice.
 *
 * WHY ITS OWN STORE NAMESPACE (`openMirrorIntentStore`, not the v2 queue). The v2 queue (P3) is the
 * Supabase-master queue: its drainer sends whatever it holds. An intent may describe a write whose
 * Firestore commit never happened; in that queue it would be sent unconditionally. Here, nothing is
 * ever sent without the Firestore witness, and nothing is sent by this module at all — the v1 outbox
 * does the sending, so there is one replay path in Firestore mode, not two.
 *
 * SCOPE: CREATES ONLY, BY CONSTRUCTION. "The document exists" proves a create committed. It proves
 * nothing about an update (the document existed before it) or a delete (the opposite question), so an
 * intent for those would need a different witness; this API cannot express one. `saveNewSale`
 * (`sales.create`, the writer of the measured loss) is the first caller. Stage-0 defect 1 names every
 * awaited writer; each create joins by stating its label, document and collection, and each
 * update/delete needs its own witness design first.
 *
 * DARK, AND OFF UNTIL AN OPERATOR TURNS IT ON (review 1). Remote Config `mirror_intent_org_allowlist`
 * (the `dual_write_org_allowlist` template) names the organizations that persist intents: a JSON array
 * of org firestore ids, or `"*"`. Unset, empty, malformed or unreadable is OFF, and OFF is today's code
 * path exactly. ATL-2052 widens it by ONE operator key: `mirror_intent_sb_readers=on` also covers every
 * organization that READS Supabase (`useSupabaseForData` on the session's organization) — the orgs a
 * lost mirror hurts, since their books are the Supabase copy. Unset is off (the allowlist alone). Both
 * keys are read RC-first with the ATL-911 backend fallback (`mirrorIntentPolicy.ts`). An OFF organization: `saveNewSale` asks `isMirrorIntentActive` synchronously and, when it says no, neither
 * builds the request early nor awaits anything. The flag gates PERSISTING only: recovery always drains
 * the intents already written, so turning the flag off never strands one. It is read synchronously
 * (`getRemoteConfigValue`), never awaited on the checkout path. Canary plan: test orgs, then a few
 * offline-prone Supabase-read tills, then `"*"` once the PostHog counters below look healthy.
 *
 * NEVER IN THE WAY OF A SALE. Persisting is capped (`PERSIST_CAP_MS`, 400 ms against a measured 2–6 ms
 * happy path) and a CIRCUIT BREAKER trips on the first slow persist, failed append or unavailable
 * store: from then on this session skips the intent with zero added latency (the same synchronous
 * `isMirrorIntentActive` answers no). The trip is reported once.
 *
 * TELEMETRY. Sentry ingestion has been dead since 2026-09-10, so every count that matters is also a
 * PostHog operational event carrying the organization and the running bundle:
 * `mirror_intent_recovered`, `_expired`, `_not_queueable`, `_persist_slow`, `_append_failed`,
 * `_breaker_tripped`, `_discarded_absent` (ATL-2052: the ABSENT discard used to be a breadcrumb only,
 * so a till whose batches the server refused lost intents with nothing countable anywhere). Once per
 * session the app also reports WHETHER intents are on for its organization, and why
 * (`mirror_intent_session`, `global/analytics/mirrorIntentTelemetry.ts`): a canary is certified by
 * tills that report themselves ON, never by the flag having been set.
 *
 * THE AGE CLOCK (decided, review 1). An intent, and the v1 entry recovered from it, keep the WRITE's
 * age, and both expire at `MAX_AGE_MS` (7 days), the v1 outbox's own bound. So a till offline for more
 * than seven days loses the mirror of a sale whose app died — loudly (Sentry + `mirror_intent_expired`,
 * or the v1 drop report) — exactly as the same till loses the mirror of a sale whose app did NOT die
 * (the live path queues it in v1 at the write's time; stage-0 defect 6). Recovery deliberately gives a
 * crashed write neither more nor less than the live path would have; lengthening the bound is a v1
 * decision, not a P7 one.
 *
 * OUTBOX v2 OBLIGATIONS (the store's docblock) this module keeps: it never caches the store across a
 * `close()` (it asks the opener every time); it treats `OutboxAppendIndeterminateError` as "maybe
 * persisted" (never re-issued under a new id — the witness decides a late-appearing one); it never
 * deletes a corrupt or stray record (it reports it and leaves it for an operator); it never replays a
 * `foreignInstall` intent (a queue restored from a backup is for an operator to judge; it is reported).
 */
import * as Sentry from '@sentry/react-native'
import type { ReplayableRequestConfig } from '@atlas/api-client'

import type { JsonObject, JsonValue, NewOutboxEntry, OutboxEntryV2, OutboxRequest } from '../Write/outboxEntry'
import { isStorageSafeId } from '../Write/outboxEntry'
import type { OutboxSlot, OutboxStore } from '../Write/outboxStore'
import { isMirrorIntentSbReadersOn, readMirrorIntentPolicy, type MirrorIntentPolicy } from './mirrorIntentPolicy'

// ============================================================================
// Tuning
// ============================================================================

/**
 * How long the write path waits for the intent to be persisted before committing anyway. A local
 * file or IndexedDB write takes milliseconds; this bounds a wedged or slow store, never a healthy one.
 */
export const PERSIST_CAP_MS = 400
/** The Remote Config kill switch (see the module docblock), and ATL-2052's Supabase-readers key. */
export { MIRROR_INTENT_ALLOWLIST_KEY, MIRROR_INTENT_SB_READERS_KEY } from './mirrorIntentPolicy'
/** A live dual-write success also runs a recovery pass, at most this often (the network is up). */
export const LIVE_SUCCESS_RECOVERY_INTERVAL_MS = 60 * 1000
/**
 * An intent whose document reads ABSENT is discarded only when this process saw it absent at least
 * this long before, too. A web tab that died with its commit already on the wire can have it land
 * after the first read; minutes are far beyond any request. Shorter than `RETRY_MS`, so the next
 * scheduled pass confirms it.
 */
export const ABSENT_CONFIRM_MS = 4 * 60 * 1000
/** Unanswerable intents are dropped (loudly) after this — the v1 outbox's own `MAX_AGE_MS`. */
export const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
/** How soon a pass that left intents behind (unknown, or absent inside the grace) runs again. */
export const RETRY_MS = 5 * 60 * 1000
/** Owner-liveness lock prefix (web): held by a tab for its whole life once it has persisted an intent. */
export const OWNER_LOCK_PREFIX = 'atlas-mirror-intent-owner-'
/** Serialises recovery passes across tabs (web). */
export const RECOVERY_LOCK_NAME = 'atlas-mirror-intent-recovery'

const INTENT_MARKER_VERSION = 1

// ============================================================================
// Types
// ============================================================================

/** What Firestore says about the document an intent's create wrote (see the module docblock). */
export type WitnessVerdict = 'present' | 'pending' | 'absent' | 'unknown'

/** Where the created document lives: `organizations/<org>/<collection>/<documentId>`. */
export interface MirrorIntentWitness {
  collection: string
}

export interface MirrorIntentSpec {
  /** The dual-write label, exactly as the live mirror uses it (`sales.create`). */
  label: string
  /** The device-minted Firestore id of the document the batch creates — the backend's idempotency key. */
  documentId: string
  witness: MirrorIntentWitness
  /**
   * The organization whose collection the document is written to: the writer's own `db` handle
   * (`organizations/<id>`). Falls back to the session's. Stated by the writer because a Supabase-read
   * org cold-starting offline may never get `organizationAtom` set (its priming read can miss the
   * cache), while the handle it writes through is always there (review 1, S5).
   */
  orgFirestoreId?: string
  /**
   * The mirror request exactly as the live call will send it — `captureWriteRequest` over the same
   * endpoint method and DTO. Rejecting means the live call would be refused client-side too.
   */
  capture: () => Promise<ReplayableRequestConfig>
}

export interface MirrorIntentHandle {
  /**
   * The mirror call RETURNED (whatever its result). The intent is removed. Fire-and-forget: the
   * write path never waits on it.
   */
  release(): void
  /**
   * The commit or the mirror call THREW. The intent is kept and handed to recovery, whose Firestore
   * witness decides: committed → queued; never committed → discarded.
   */
  abandon(): void
  /** Resolves with whether the intent reached storage (tests and reporting; never awaited by writers). */
  readonly persisted: Promise<boolean>
}

type LockCallback = (lock: unknown) => Promise<unknown> | unknown
export interface LockManagerLike {
  request(name: string, options: { ifAvailable?: boolean }, callback: LockCallback): Promise<unknown>
}

export interface RecoveryOutcome {
  /** Handed to the v1 outbox (the ATL-1459 window was hit). */
  recovered: number
  /** Witnessed absent twice, `ABSENT_CONFIRM_MS` apart: the commit never happened. */
  discardedAbsent: number
  /** Unanswered past MAX_AGE_MS: dropped loudly. */
  expired: number
  /** The v1 outbox refused it (it reported why): removed, as the live path's unqueueable failure. */
  notQueueable: number
  /** Kept for a later pass: pending, unknown, absent not yet confirmed, or an enqueue that threw. */
  kept: number
  /** Writers still running: this context's live intents, or another tab's that is alive. */
  skippedLive: number
  /** Held for an operator: restored from another install. */
  heldForeign: number
  /** Held for an operator: unreadable record, or an entry this module cannot read as an intent. */
  heldUnreadable: number
}

export interface MirrorIntentJournalDeps {
  /** The store, asked for on EVERY operation (never kept across a `close()`). */
  openStore: () => OutboxStore
  /** This JS context's id; storage-safe. A new process or a web tab is a new context. */
  contextId: string
  /** Web Locks (web only). `null` where there is none; on web that disables intents (see `persist`). */
  locks: LockManagerLike | null
  /** The session's organization (Firestore id), or undefined before it is known. */
  sessionOrgId: () => string | undefined
  readWitness: (witness: MirrorIntentWitness, documentId: string, orgFirestoreId: string) => Promise<WitnessVerdict>
  enqueueRecovered: (
    label: string,
    request: ReplayableRequestConfig,
    context: { firestoreId: string; orgFirestoreId: string; enqueuedAt: number },
  ) => Promise<boolean>
  /** After a recovered entry is queued: kick the v1 drain. */
  afterRecovered: () => void
  now?: () => number
  /** Arms the retry of a pass that left intents behind. Tests pass a recorder. */
  schedule?: (fn: () => void, ms: number) => void
  /** `PERSIST_CAP_MS` unless a test shortens it. */
  persistCapMs?: number
  /**
   * The raw `mirror_intent_org_allowlist` value, read synchronously. Default: Remote Config, else the
   * ATL-911 backend fallback (`readMirrorIntentPolicy`).
   */
  allowlist?: () => string
  /** ATL-2052 — the raw `mirror_intent_sb_readers` value. Default: as `allowlist`. */
  sbReaders?: () => string
  /**
   * ATL-2052 — does this organization READ Supabase? Default: the session's organization document
   * (`organizationAtom.useSupabaseForData`, ATL-886's client-side source of the read cutover) for
   * that same organization; anything else — an org that is not the session's, an atom not yet
   * primed — is `false` (not covered by this half).
   */
  orgReadsSupabase?: (orgFirestoreId: string) => boolean
  /** PostHog operational event sink. Default: `captureOperationalEvent` + the running bundle. */
  emit?: (event: MirrorIntentEvent, properties: Record<string, string | number | boolean | null>) => void
}

export type MirrorIntentEvent =
  | 'mirror_intent_recovered'
  | 'mirror_intent_expired'
  | 'mirror_intent_not_queueable'
  | 'mirror_intent_persist_slow'
  | 'mirror_intent_append_failed'
  | 'mirror_intent_breaker_tripped'
  | 'mirror_intent_discarded_absent'

/**
 * Who `mirror_intent_org_allowlist` turns on: `"*"` / `["*"]` → everyone; a JSON array of strings →
 * those orgs; ANYTHING else (unset, empty, malformed, a non-string member) → nobody.
 */
export function parseMirrorIntentAllowlist(raw: unknown): { all: boolean; ids: ReadonlySet<string> } {
  const off = { all: false, ids: new Set<string>() }
  if (typeof raw !== 'string') return off
  const value = raw.trim()
  if (value === '*') return { all: true, ids: new Set<string>() }
  if (!value.startsWith('[')) return off
  try {
    const parsed: unknown = JSON.parse(value)
    if (!Array.isArray(parsed) || !parsed.every((id) => typeof id === 'string' && id.length > 0)) return off
    const ids = parsed as string[]
    return ids.includes('*') ? { all: true, ids: new Set<string>() } : { all: false, ids: new Set(ids) }
  } catch {
    return off
  }
}

// ============================================================================
// Pure helpers
// ============================================================================

interface IntentMarker {
  mirrorIntent: typeof INTENT_MARKER_VERSION
  owner: string
  witness: MirrorIntentWitness
}

/** The intent-specific fields, read back from `effects` — `null` for anything this module did not write. */
export function readIntentMarker(entry: Pick<OutboxEntryV2, 'effects' | 'kind'>): IntentMarker | null {
  if (entry.kind !== 'create') return null
  const effects = entry.effects
  if (!effects || typeof effects !== 'object' || Array.isArray(effects)) return null
  const e = effects as JsonObject
  if (e.mirrorIntent !== INTENT_MARKER_VERSION) return null
  if (!isStorageSafeId(e.owner)) return null
  const witness = e.witness
  if (!witness || typeof witness !== 'object' || Array.isArray(witness)) return null
  const collection = (witness as JsonObject).collection
  if (typeof collection !== 'string' || !isFirestoreSegment(collection)) return null
  return { mirrorIntent: INTENT_MARKER_VERSION, owner: e.owner, witness: { collection } }
}

/** A single Firestore path segment (no `/`, not `.`/`..`, non-empty). */
function isFirestoreSegment(value: string): boolean {
  return value.length > 0 && value.length <= 1500 && !value.includes('/') && value !== '.' && value !== '..'
}

/** The persisted request: the captured config narrowed to what the v2 entry schema holds. */
function toOutboxRequest(config: ReplayableRequestConfig): OutboxRequest {
  const request: OutboxRequest = { method: config.method as OutboxRequest['method'], url: config.url }
  if (config.data !== undefined) request.data = config.data as JsonValue
  if (config.params !== undefined) request.params = config.params as JsonObject
  return request
}

/** The request handed back to the v1 outbox: the stored one, verbatim. */
function toReplayable(request: OutboxRequest): ReplayableRequestConfig {
  const config: ReplayableRequestConfig = { method: request.method, url: request.url }
  if (request.data !== undefined) config.data = request.data
  if (request.params !== undefined) config.params = request.params
  return config
}

/** A storage-safe id for a JS context: time prefix + randomness (Hermes has no crypto.randomUUID). */
export function newContextId(random: () => number = Math.random, now: () => number = Date.now): string {
  let tail = ''
  for (let i = 0; i < 3; i++) tail += Math.floor(random() * 2 ** 26).toString(36).padStart(6, '0')
  return `c${now().toString(36)}${tail}`
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  try {
    return String(error)
  } catch {
    return 'unknown error'
  }
}

/** Both policy keys, read synchronously: the activated Remote Config, else the ATL-911 backend copy. */
function appPolicy(): MirrorIntentPolicy {
  // Lazily required: the Firestore module binds the platform SDK; the journal must load without it.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { getRemoteConfigValue } = require('./firebaseFirestore') as { getRemoteConfigValue: (key: string) => unknown }
  return readMirrorIntentPolicy(getRemoteConfigValue)
}

/**
 * ATL-2052 — the organization reads Supabase: the session's organization document says so, for THIS
 * organization. Deliberately not `shouldUseSupabase()`: its device override and local-dev switch are
 * about one device, and coverage is about the organization's books.
 */
function sessionOrgReadsSupabase(orgFirestoreId: string): boolean {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const types = require('./types') as { getOrganization: () => { id?: unknown; useSupabaseForData?: unknown } | null }
    const org = types.getOrganization()
    return org?.id === orgFirestoreId && org.useSupabaseForData === true
  } catch {
    return false
  }
}

/** PostHog, with the running bundle's identity (the `ota-coverage` property names). */
function emitOperational(event: MirrorIntentEvent, properties: Record<string, string | number | boolean | null>): void {
  let bundle: Record<string, unknown>
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { runningBundleProperties } = require('../analytics/writeBundleCapability') as {
      runningBundleProperties: () => Record<string, unknown>
    }
    bundle = runningBundleProperties()
  } catch {
    bundle = { bundle_identity_source: 'unknown' }
  }
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { captureOperationalEvent } = require('../analytics/operationalTelemetry') as {
    captureOperationalEvent: (event: string, properties: Record<string, unknown>) => void
  }
  captureOperationalEvent(event, { ...bundle, ...properties })
}

const TIMED_OUT = Symbol('mirror-intent-timed-out')

async function withinCap<T>(work: Promise<T>, ms: number): Promise<T | typeof TIMED_OUT> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race([
      work,
      new Promise<typeof TIMED_OUT>((resolve) => {
        timer = setTimeout(() => resolve(TIMED_OUT), ms)
      }),
    ])
  } finally {
    clearTimeout(timer)
  }
}

// ============================================================================
// Reporting (never throws)
// ============================================================================

const reportedOnce = new Set<string>()

function reportOnce(key: string, message: string, level: 'warning' | 'error', context: Record<string, unknown>): void {
  if (reportedOnce.has(key)) return
  reportedOnce.add(key)
  report(message, level, context)
}

function report(message: string, level: 'info' | 'warning' | 'error', context: Record<string, unknown>): void {
  try {
    const line = `[mirrorIntent] ${message}`
    // eslint-disable-next-line no-console
    if (level === 'error') console.error(line, JSON.stringify(context))
    // eslint-disable-next-line no-console
    else console.log(line, JSON.stringify(context))
    Sentry.captureMessage(`mirror intent: ${message}`, {
      level,
      tags: {
        mirror_intent: String(context.kind ?? 'event'),
        ...(typeof context.label === 'string' ? { dual_write: context.label } : {}),
        ...(typeof context.orgFirestoreId === 'string' ? { dual_write_org: context.orgFirestoreId } : {}),
      },
      fingerprint: ['mirror-intent', String(context.kind ?? 'event'), String(context.label ?? '')],
      contexts: { mirror_intent: context },
    })
  } catch {
    // Reporting must never break the write path or the recovery pass it describes.
  }
}

function breadcrumb(message: string, data: Record<string, unknown>): void {
  try {
    Sentry.addBreadcrumb({ category: 'dual_write.mirror_intent', level: 'info', message, data })
  } catch {
    // As above.
  }
}

/** TEST-ONLY: forget which conditions were already reported this session. */
export function resetMirrorIntentReportingForTests(): void {
  reportedOnce.clear()
}

// ============================================================================
// The journal
// ============================================================================

const NOOP_HANDLE: MirrorIntentHandle = {
  release() {},
  abandon() {},
  persisted: Promise.resolve(false),
}

/** Why `active` answers what it answers (ATL-2052 session report). */
export interface MirrorIntentActivation {
  /** `active(org)`: what a writer would be told right now. */
  active: boolean
  /** The org is on `mirror_intent_org_allowlist` (or it is `"*"`). */
  allowlisted: boolean
  /** The reason the session's circuit breaker tripped, or `null` while it has not. */
  breakerTripped: string | null
  /** ATL-2052 — which half of the predicate covers the org, or `null` when neither does. */
  coveredBy: 'allowlist' | 'sb-readers' | null
  /** ATL-2052 — `mirror_intent_sb_readers` reads `on`. */
  sbReadersOn: boolean
  /** ATL-2052 — the organization reads Supabase (as `orgReadsSupabase` answers). */
  orgReadsSupabase: boolean
}

export interface MirrorIntentJournal {
  /**
   * Synchronous: would `persist` write an intent for this org right now? (Allowlisted, and the
   * breaker has not tripped.) Writers ask it first so OFF costs nothing at all.
   */
  active(orgFirestoreId: string | undefined): boolean
  /** Synchronous, never throws: `active` and the two facts it is made of. */
  describe(orgFirestoreId: string | undefined): MirrorIntentActivation
  persist(spec: MirrorIntentSpec): Promise<MirrorIntentHandle>
  recover(orgFirestoreId: string): Promise<RecoveryOutcome>
  /** TEST-ONLY: the ids this context's writers still hold. */
  liveIntentIds(): string[]
}

export function createMirrorIntentJournal(deps: MirrorIntentJournalDeps): MirrorIntentJournal {
  const now = deps.now ?? Date.now
  const schedule = deps.schedule ?? ((fn: () => void, ms: number) => void setTimeout(fn, ms))
  const persistCapMs = deps.persistCapMs ?? PERSIST_CAP_MS
  const readAllowlist = deps.allowlist ?? (() => appPolicy().allowlistRaw)
  const readSbReaders = deps.sbReaders ?? (() => appPolicy().sbReadersRaw)
  const readsSupabase = deps.orgReadsSupabase ?? sessionOrgReadsSupabase
  const emit = deps.emit ?? emitOperational
  /** The circuit breaker (module docblock): once tripped, nothing more is persisted this session. */
  let tripped: string | null = null

  function safeEmit(event: MirrorIntentEvent, properties: Record<string, string | number | boolean | null>): void {
    try {
      emit(event, properties)
    } catch {
      // Telemetry never breaks the write path or a recovery pass.
    }
  }

  function trip(reason: string, context: { label: string; orgFirestoreId: string }): void {
    if (tripped) return
    tripped = reason
    report(`circuit breaker tripped (${reason}): intents skipped for the rest of this session`, 'warning', {
      kind: 'breaker-tripped',
      reason,
      ...context,
    })
    safeEmit('mirror_intent_breaker_tripped', { reason, organization_id: context.orgFirestoreId, label: context.label })
  }

  function allowlisted(org: string | undefined): boolean {
    if (!org) return false
    try {
      const list = parseMirrorIntentAllowlist(readAllowlist())
      return list.all || list.ids.has(org)
    } catch {
      return false // unreadable is OFF
    }
  }

  function sbReadersOn(): boolean {
    try {
      return isMirrorIntentSbReadersOn(readSbReaders())
    } catch {
      return false // unreadable is OFF
    }
  }

  function orgReadsSb(org: string | undefined): boolean {
    if (!org) return false
    try {
      return readsSupabase(org)
    } catch {
      return false
    }
  }

  /** ATL-2052: allowlisted OR (reads Supabase AND `mirror_intent_sb_readers=on`). */
  function coverage(org: string | undefined): MirrorIntentActivation['coveredBy'] {
    if (allowlisted(org)) return 'allowlist'
    if (sbReadersOn() && orgReadsSb(org)) return 'sb-readers'
    return null
  }

  function active(org: string | undefined): boolean {
    return tripped === null && coverage(org) !== null
  }

  function describe(org: string | undefined): MirrorIntentActivation {
    const coveredBy = coverage(org)
    return {
      active: tripped === null && coveredBy !== null,
      allowlisted: coveredBy === 'allowlist',
      breakerTripped: tripped,
      coveredBy,
      sbReadersOn: sbReadersOn(),
      orgReadsSupabase: orgReadsSb(org),
    }
  }
  /** ABSENT discards this session, across organizations (the `mirror_intent_discarded_absent` counter). */
  let discardedAbsentInSession = 0
  /** Intents whose writer (in THIS context) has not settled yet. Recovery never touches them. */
  const live = new Set<string>()
  let counter = 0
  let ownerLock: Promise<boolean> | null = null
  const running = new Map<string, Promise<RecoveryOutcome>>()
  /** Organizations with a retry pass scheduled (review 2: one per org, not one per journal). */
  const retryArmed = new Set<string>()
  /** When this process first saw each intent's document ABSENT (see `ABSENT_CONFIRM_MS`). */
  const absentSince = new Map<string, number>()

  /**
   * Web: hold `OWNER_LOCK_PREFIX + contextId` for the life of this tab, so another tab's recovery can
   * tell a running writer from a dead one. Granted before the first intent is written, and never
   * released: the browser releases it when the tab goes.
   */
  function ensureOwnerLock(): Promise<boolean> {
    if (!ownerLock) {
      const locks = deps.locks
      ownerLock = !locks
        ? Promise.resolve(false)
        : new Promise<boolean>((resolve) => {
            locks
              .request(OWNER_LOCK_PREFIX + deps.contextId, {}, () => {
                resolve(true)
                return new Promise<never>(() => undefined)
              })
              .catch(() => resolve(false))
          })
    }
    return ownerLock
  }

  /** Web: is the tab that wrote this intent still running? Any doubt answers yes (never recover a live one). */
  async function ownerAlive(owner: string): Promise<boolean> {
    const locks = deps.locks
    if (!locks) return true
    let alive = true
    try {
      await locks.request(OWNER_LOCK_PREFIX + owner, { ifAvailable: true }, (lock) => {
        alive = lock === null
      })
    } catch {
      alive = true
    }
    return alive
  }

  async function prepareAndAppend(spec: MirrorIntentSpec, org: string): Promise<OutboxEntryV2 | null> {
    let config: ReplayableRequestConfig
    try {
      config = await spec.capture()
    } catch (error) {
      // The live call fails the same way (a client-side refusal), and is classified and reported there.
      breadcrumb(`${spec.label}: mirror request not capturable, no intent`, {
        label: spec.label,
        error: errorMessage(error),
      })
      return null
    }
    let store: OutboxStore
    try {
      store = deps.openStore()
      if (!store || typeof store.append !== 'function') throw new Error('the store opener returned no store')
    } catch (error) {
      reportOnce('store-unavailable', 'intent store unavailable — this session writes unprotected', 'warning', {
        kind: 'store-unavailable',
        label: spec.label,
        error: errorMessage(error),
      })
      safeEmit('mirror_intent_append_failed', { organization_id: org, label: spec.label, error: 'store-unavailable' })
      trip('store-unavailable', { label: spec.label, orgFirestoreId: org })
      return null
    }
    if (store.engine === 'indexeddb') {
      // Without an owner lock another tab could judge this tab's pending commit ABSENT and discard its
      // intent, or recover it while this tab also sends it. No lock, no intent: today's behaviour.
      if (!deps.locks) {
        reportOnce('no-web-locks', 'web locks unavailable — mirror intents disabled', 'warning', {
          kind: 'no-web-locks',
        })
        return null
      }
      if (!(await ensureOwnerLock())) {
        reportOnce('owner-lock-refused', 'owner lock refused — mirror intents disabled', 'warning', {
          kind: 'owner-lock-refused',
        })
        return null
      }
    }
    counter += 1
    const entryId = `mi-${deps.contextId}-${counter}`
    const marker: IntentMarker = { mirrorIntent: INTENT_MARKER_VERSION, owner: deps.contextId, witness: spec.witness }
    const entry: NewOutboxEntry = {
      v: 2,
      entryId,
      orgFirestoreId: org,
      label: spec.label,
      documentId: spec.documentId,
      relatedIds: [],
      kind: 'create',
      request: toOutboxRequest(config),
      effects: marker as unknown as JsonObject,
      enqueuedAt: now(),
      bundleId: null,
      origin: 'live',
      state: 'pending',
      attempts: 0,
      lastError: null,
      heldSince: null,
      nextAttemptAt: 0,
    }
    // Live BEFORE the append starts: a recovery pass must never see this entry as abandoned.
    live.add(entryId)
    try {
      return await store.append(entry)
    } catch (error) {
      // `OutboxAppendIndeterminateError` (native) may still publish at the next recovery of the store:
      // it is not re-issued (that would be two intents for one write); if it appears, it is an ordinary
      // intent whose writer is gone, and the witness decides it like any other.
      live.delete(entryId)
      reportOnce(`append-failed:${errorMessage(error)}`, 'intent not persisted — this write runs unprotected', 'warning', {
        kind: 'append-failed',
        label: spec.label,
        orgFirestoreId: org,
        error: errorMessage(error),
      })
      safeEmit('mirror_intent_append_failed', { organization_id: org, label: spec.label, error: errorMessage(error).slice(0, 200) })
      trip('append-failed', { label: spec.label, orgFirestoreId: org })
      return null
    }
  }

  async function persist(spec: MirrorIntentSpec): Promise<MirrorIntentHandle> {
    try {
      const org = spec.orgFirestoreId ?? deps.sessionOrgId()
      // The kill switch and the breaker: synchronous, before anything is captured or awaited.
      if (!active(org)) return NOOP_HANDLE
      if (!org || !isStorageSafeId(org)) {
        // The v1 outbox refuses an entry without an organization too (`no-organization`).
        breadcrumb(`${spec.label}: no organization, no intent`, { label: spec.label })
        return NOOP_HANDLE
      }
      if (!isFirestoreSegment(spec.documentId) || !isFirestoreSegment(spec.witness.collection)) {
        reportOnce(`bad-spec:${spec.label}`, 'intent spec names no single document — no intent', 'error', {
          kind: 'bad-spec',
          label: spec.label,
        })
        return NOOP_HANDLE
      }

      const appended = prepareAndAppend(spec, org).catch(() => null)
      const persisted = appended.then((entry) => entry !== null)
      if ((await withinCap(appended, persistCapMs)) === TIMED_OUT) {
        // The commit goes ahead unprotected (never blocked by this module). The append still finishes,
        // and the handle below settles whatever it produced.
        reportOnce('persist-slow', `intent not persisted within ${persistCapMs}ms — committing anyway`, 'warning', {
          kind: 'persist-slow',
          label: spec.label,
          orgFirestoreId: org,
        })
        safeEmit('mirror_intent_persist_slow', { organization_id: org, label: spec.label, cap_ms: persistCapMs })
        trip('persist-slow', { label: spec.label, orgFirestoreId: org })
      }

      let settled = false
      return {
        persisted,
        release() {
          if (settled) return
          settled = true
          void appended.then(async (entry) => {
            if (!entry) return
            try {
              await deps.openStore().remove(org, entry.entryId)
              live.delete(entry.entryId)
            } catch (error) {
              // Left LIVE for this session, so no pass here re-sends a mirror that ran. The next launch
              // recovers it, and the backend answers the replayed create from the row it has.
              reportOnce(`remove-failed:${errorMessage(error)}`, 'settled intent could not be removed', 'warning', {
                kind: 'remove-failed',
                label: spec.label,
                orgFirestoreId: org,
                entryId: entry.entryId,
                error: errorMessage(error),
              })
            }
          })
        },
        abandon() {
          if (settled) return
          settled = true
          void appended.then((entry) => {
            if (!entry) return
            live.delete(entry.entryId)
            breadcrumb(`${spec.label}: writer abandoned its intent — the witness decides`, {
              label: spec.label,
              entryId: entry.entryId,
            })
            void recover(org).catch(() => undefined)
          })
        },
      }
    } catch (error) {
      reportOnce(`persist-threw:${errorMessage(error)}`, 'intent machinery threw — this write runs unprotected', 'error', {
        kind: 'persist-threw',
        label: spec.label,
        error: errorMessage(error),
      })
      return NOOP_HANDLE
    }
  }

  function armRetry(org: string): void {
    if (retryArmed.has(org)) return
    retryArmed.add(org)
    schedule(() => {
      retryArmed.delete(org)
      void recover(org).catch(() => undefined)
    }, RETRY_MS)
  }

  async function recoverNow(org: string): Promise<RecoveryOutcome> {
    const outcome: RecoveryOutcome = {
      recovered: 0,
      discardedAbsent: 0,
      expired: 0,
      notQueueable: 0,
      kept: 0,
      skippedLive: 0,
      heldForeign: 0,
      heldUnreadable: 0,
    }
    const store = deps.openStore()
    const slots: OutboxSlot[] = await store.list(org)
    /** A removal that throws keeps the intent for the next pass; it never aborts this one (review 1). */
    const removed = async (entryId: string): Promise<boolean> => {
      try {
        await store.remove(org, entryId)
        return true
      } catch (error) {
        outcome.kept += 1
        reportOnce(`recovery-remove-failed:${errorMessage(error)}`, 'intent could not be removed — kept for the next pass', 'warning', {
          kind: 'recovery-remove-failed',
          orgFirestoreId: org,
          entryId,
          error: errorMessage(error),
        })
        return false
      }
    }
    for (const slot of slots) {
      if (slot.status !== 'ok') {
        outcome.heldUnreadable += 1
        reportOnce(`unreadable:${org}:${slot.ref}`, `unreadable intent record held for an operator (${slot.reason})`, 'error', {
          kind: 'unreadable',
          orgFirestoreId: org,
          ref: slot.ref,
          entryId: slot.entryId,
          reason: slot.reason,
        })
        continue
      }
      const entry = slot.entry
      const marker = readIntentMarker(entry)
      if (!marker) {
        outcome.heldUnreadable += 1
        reportOnce(`not-an-intent:${org}:${entry.entryId}`, 'record is not a mirror intent — held for an operator', 'error', {
          kind: 'not-an-intent',
          orgFirestoreId: org,
          entryId: entry.entryId,
          label: entry.label,
        })
        continue
      }
      if (live.has(entry.entryId)) {
        outcome.skippedLive += 1
        continue
      }
      if (marker.owner !== deps.contextId && store.engine === 'indexeddb' && (await ownerAlive(marker.owner))) {
        outcome.skippedLive += 1
        continue
      }
      if (slot.foreignInstall) {
        outcome.heldForeign += 1
        reportOnce(`foreign:${org}:${entry.entryId}`, 'intent restored from another install — held for an operator', 'error', {
          kind: 'foreign-install',
          orgFirestoreId: org,
          entryId: entry.entryId,
          installId: entry.installId,
          label: entry.label,
          documentId: entry.documentId,
        })
        continue
      }
      const age = now() - entry.enqueuedAt
      // The witness reads through the SESSION's organization; a pass for another org cannot answer it.
      // Such an intent still has its loud 7-day expiry (review 2): an org this device signed out of
      // must not keep an unanswerable intent forever.
      if (deps.sessionOrgId() !== org) {
        if (age >= MAX_AGE_MS) {
          if (!(await removed(entry.entryId))) continue
          outcome.expired += 1
          report('intent of another organization unanswerable for 7 days — dropped; its mirror may be lost', 'error', {
            kind: 'expired',
            label: entry.label,
            orgFirestoreId: org,
            entryId: entry.entryId,
            documentId: entry.documentId,
            ageMs: age,
          })
          safeEmit('mirror_intent_expired', { organization_id: org, label: entry.label, age_ms: age, verdict: 'not-session-org' })
          continue
        }
        outcome.kept += 1
        continue
      }
      let verdict: WitnessVerdict
      try {
        verdict = await deps.readWitness(marker.witness, entry.documentId, org)
      } catch {
        verdict = 'unknown'
      }
      const context = {
        label: entry.label,
        orgFirestoreId: org,
        entryId: entry.entryId,
        documentId: entry.documentId,
        ageMs: age,
      }
      if (verdict === 'present') {
        // The listing is a snapshot: a writer of this context may have released (sent) it since. Its
        // release removes the intent BEFORE leaving the live set, so "still stored and not live" means
        // no writer is sending it.
        let still: OutboxEntryV2 | null
        try {
          still = await store.get(org, entry.entryId)
        } catch {
          still = null
        }
        if (!still || live.has(entry.entryId)) {
          outcome.skippedLive += 1
          continue
        }
        let queued: boolean
        try {
          queued = await deps.enqueueRecovered(entry.label, toReplayable(entry.request), {
            firestoreId: entry.documentId,
            orgFirestoreId: org,
            enqueuedAt: entry.enqueuedAt,
          })
        } catch (error) {
          // The v1 queue could not be written: keep the intent, it is still the only copy.
          outcome.kept += 1
          reportOnce(`enqueue-threw:${errorMessage(error)}`, 'recovered intent could not be queued yet — kept', 'warning', {
            kind: 'enqueue-threw',
            ...context,
            error: errorMessage(error),
          })
          continue
        }
        // Queued (or refused for good) in v1 either way. An intent that cannot be removed now is removed
        // by a later pass, whose re-enqueue supersedes this one (same label and document).
        await removed(entry.entryId)
        const props = { organization_id: org, label: entry.label, age_ms: age }
        if (queued) {
          outcome.recovered += 1
          report('recovered a mirror the app died before sending (ATL-1459)', 'warning', { kind: 'recovered', ...context })
          safeEmit('mirror_intent_recovered', props)
        } else {
          // Refused by the v1 queue, which has already reported why (too large, overflow). The live path's
          // unqueueable failure ends the same way.
          outcome.notQueueable += 1
          report('recovered mirror refused by the outbox — lost', 'error', { kind: 'not-queueable', ...context })
          safeEmit('mirror_intent_not_queueable', props)
        }
        continue
      }
      if (verdict === 'absent') {
        const firstSeen = absentSince.get(entry.entryId)
        if (firstSeen === undefined) {
          absentSince.set(entry.entryId, now())
          outcome.kept += 1
        } else if (now() - firstSeen >= ABSENT_CONFIRM_MS) {
          if (!(await removed(entry.entryId))) continue
          absentSince.delete(entry.entryId)
          outcome.discardedAbsent += 1
          discardedAbsentInSession += 1
          breadcrumb(`${entry.label}: the commit never happened — intent discarded`, context)
          // ATL-2052: LOUD. The discard is correct (sending it would book a Supabase-only record), but
          // it means a batch this till committed never reached the server — a rules refusal, or a
          // process that died before the SDK queued it. Either is a write the merchant believes was
          // made; it must be countable per organization, not only a breadcrumb that rides along with
          // whatever exception happens next. The behaviour (two observations, then discard) is unchanged.
          report('the commit never reached the server — intent discarded (no Supabase-only record)', 'warning', {
            kind: 'discarded-absent',
            witnessCollection: marker.witness.collection,
            ...context,
          })
          safeEmit('mirror_intent_discarded_absent', {
            organization_id: org,
            label: entry.label,
            witness_collection: marker.witness.collection,
            age_ms: age,
            session_absent_count: discardedAbsentInSession,
          })
        } else {
          outcome.kept += 1
        }
        continue
      }
      absentSince.delete(entry.entryId)
      if (age >= MAX_AGE_MS) {
        if (!(await removed(entry.entryId))) continue
        outcome.expired += 1
        report('intent unanswerable for 7 days — dropped; its mirror may be lost', 'error', { kind: 'expired', ...context })
        safeEmit('mirror_intent_expired', { organization_id: org, label: entry.label, age_ms: age, verdict })
        continue
      }
      outcome.kept += 1
    }
    if (outcome.recovered > 0) deps.afterRecovered()
    // Only the session's org is retried on a timer: another org's intents cannot be answered here, and
    // they are visited (for their expiry) by every session pass instead — see `sweepOtherOrgs`.
    if (outcome.kept > 0 && deps.sessionOrgId() === org) armRetry(org)
    return outcome
  }

  /**
   * Every OTHER organization with intents on this device gets an expiry-only pass (review 2): a device
   * that switched orgs would otherwise never list the old org's intents again, and they would never
   * reach the loud 7-day expiry. Nothing in it can fail the session's pass.
   */
  async function sweepOtherOrgs(sessionOrg: string): Promise<void> {
    let organizations: string[]
    try {
      organizations = (await deps.openStore().census()).organizations
    } catch {
      return
    }
    for (const other of organizations) {
      if (other === sessionOrg) continue
      try {
        await recoverNow(other)
      } catch {
        // The next session pass tries again.
      }
    }
  }

  function recover(org: string): Promise<RecoveryOutcome> {
    const inFlight = running.get(org)
    if (inFlight) return inFlight
    const run = (async (): Promise<RecoveryOutcome> => {
      const locks = deps.locks
      if (locks && deps.openStore().engine === 'indexeddb') {
        // Two tabs recovering the same dead tab's intents would both queue them (harmless — the v1
        // queue supersedes same label + document — but pointless).
        const holder: { result?: RecoveryOutcome } = {}
        await locks.request(RECOVERY_LOCK_NAME, {}, async () => {
          holder.result = await recoverNow(org)
          await sweepOtherOrgs(org)
        })
        if (!holder.result) throw new Error('recovery lock granted without running the pass')
        return holder.result
      }
      const result = await recoverNow(org)
      await sweepOtherOrgs(org)
      return result
    })()
    running.set(org, run)
    const clear = () => {
      if (running.get(org) === run) running.delete(org)
    }
    run.then(clear, clear)
    return run
  }

  return {
    active,
    describe,
    persist,
    recover,
    liveIntentIds: () => {
      const ids: string[] = []
      for (const id of live) ids.push(id)
      return ids
    },
  }
}

// ============================================================================
// The Firestore witness
// ============================================================================

/** A witness read that does not answer in this long is `unknown` (the probe's own bound, ATL-1207). */
export const WITNESS_READ_TIMEOUT_MS = 10_000

type WitnessSnapshot = { exists?: unknown; metadata?: { hasPendingWrites?: unknown } }
type WitnessDb = {
  id?: unknown
  collection: (name: string) => { doc: (id: string) => { get: (options: { source: string }) => Promise<unknown> } }
}

/**
 * The organization this session writes through, or `undefined` when it cannot be told. `getDb()` is
 * `organizations/<user.organizationId>`, set at sign-in from the user document (Routes.js), so it is
 * there on an offline cold start; `organizationAtom` is filled by the org read and may never be (a
 * Supabase-read org whose priming read misses the cache, review 1 S5). Two sources that DISAGREE name
 * no organization: an answer about one org's document must never decide another's intent (the
 * ATL-1207 probe's rule).
 */
export function sessionWriteOrgId(): string | undefined {
  try {
    // Lazily required: `./types` reaches the Jotai atoms and the app graph (the outbox's cycle rule).
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const types = require('./types') as { getDb: () => unknown; getOrganization: () => { id?: unknown } | null }
    const dbId = (types.getDb() as { id?: unknown } | null)?.id
    const orgId = types.getOrganization()?.id
    const fromDb = typeof dbId === 'string' && dbId.length > 0 ? dbId : undefined
    const fromOrg = typeof orgId === 'string' && orgId.length > 0 ? orgId : undefined
    if (fromDb && fromOrg && fromDb !== fromOrg) return undefined
    return fromDb ?? fromOrg
  } catch {
    return undefined
  }
}

/**
 * Read `organizations/<org>/<collection>/<documentId>` the way recovery needs it (module docblock).
 *
 * The CACHE is asked first because only it knows a PENDING write the SDK is replaying. A cached copy
 * with no pending write is NOT taken as present on its own (review 1, S1): it can be the stale copy of
 * a sale another till has since deleted, and recovering it would book a deleted sale into Supabase.
 * It is confirmed with a SERVER read, like every other non-pending answer, and a server that cannot be
 * reached (offline, an error, no answer within `timeoutMs`) is `unknown`, never `present` or `absent`.
 * A cached NON-existent copy with no pending write says nothing either and also goes to the server.
 * Only a cached pending delete is answered locally (`absent`): this device has undone the create.
 */
export async function readFirestoreWitness(
  witness: MirrorIntentWitness,
  documentId: string,
  orgFirestoreId: string,
  timeoutMs: number = WITNESS_READ_TIMEOUT_MS,
): Promise<WitnessVerdict> {
  let db: WitnessDb | null
  let exists: (snapshot: WitnessSnapshot | null | undefined) => boolean
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const types = require('./types') as {
      getDb: () => unknown
      snapshotExists: (snapshot: WitnessSnapshot | null | undefined) => boolean
    }
    if (sessionWriteOrgId() !== orgFirestoreId) return 'unknown'
    db = types.getDb() as WitnessDb | null
    exists = types.snapshotExists
  } catch {
    return 'unknown'
  }
  if (!db || typeof db.collection !== 'function') return 'unknown'
  // The handle that is read must itself name the org (not only a second atom expected to agree).
  if (db.id !== orgFirestoreId) return 'unknown'
  const handle: WitnessDb = db

  const read = async (source: 'cache' | 'server'): Promise<WitnessSnapshot | null> => {
    try {
      const answer = await withinCap(handle.collection(witness.collection).doc(documentId).get({ source }), timeoutMs)
      // A read that did not answer in time is NO answer — never a non-existent document.
      return answer === TIMED_OUT ? null : ((answer as WitnessSnapshot | undefined) ?? null)
    } catch {
      // Web throws for a document that is not in its cache; offline throws for a server read. Neither
      // is an answer.
      return null
    }
  }

  const cached = await read('cache')
  if (cached && cached.metadata?.hasPendingWrites === true) {
    // The SDK holds a local write the server has not accepted: the create replaying (pending), or a
    // delete of it (this device has already undone the create).
    return exists(cached) ? 'pending' : 'absent'
  }
  const server = await read('server')
  if (!server) return 'unknown'
  return exists(server) ? 'present' : 'absent'
}

// ============================================================================
// The app's journal
// ============================================================================

function webLocks(): LockManagerLike | null {
  try {
    const nav = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator
    return nav?.locks ?? null
  } catch {
    return null
  }
}

let appJournal: MirrorIntentJournal | null = null
let appRetryTimer: ReturnType<typeof setTimeout> | null = null

function journal(): MirrorIntentJournal {
  appJournal ??= createMirrorIntentJournal({
    openStore: () => {
      // Lazily required: the store module binds the platform engine (react-native `Platform`), and a
      // failure to load it must degrade this write to today's behaviour, not fail the module graph.
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { openMirrorIntentStore } = require('../Write/store') as { openMirrorIntentStore: () => OutboxStore }
      return openMirrorIntentStore()
    },
    contextId: newContextId(),
    locks: webLocks(),
    sessionOrgId: sessionWriteOrgId,
    readWitness: readFirestoreWitness,
    schedule: (fn, ms) => {
      if (appRetryTimer) clearTimeout(appRetryTimer)
      appRetryTimer = setTimeout(() => {
        appRetryTimer = null
        fn()
      }, ms)
    },
    enqueueRecovered: (label, request, context) => {
      // Lazily required for the outbox's own cycle reason (it is imported by dualWrite.ts).
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { enqueueRecoveredMirrorIntent } = require('./dualWriteOutbox') as {
        enqueueRecoveredMirrorIntent: MirrorIntentJournalDeps['enqueueRecovered']
      }
      return enqueueRecoveredMirrorIntent(label, request, context)
    },
    afterRecovered: () => {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const { drainDualWriteOutbox } = require('./dualWriteOutbox') as {
        drainDualWriteOutbox: (trigger: 'recovered-intent') => Promise<void>
      }
      void drainDualWriteOutbox('recovered-intent').catch(() => undefined)
    },
  })
  return appJournal
}

/**
 * Synchronous, never throws: would a Firestore-mode create for this org persist a mirror intent right
 * now? The kill switch (`mirror_intent_org_allowlist`) and the circuit breaker. A writer that hears
 * "no" runs today's code path unchanged — nothing built early, nothing awaited.
 */
export function isMirrorIntentActive(orgFirestoreId: string | undefined): boolean {
  try {
    return journal().active(orgFirestoreId ?? sessionWriteOrgId())
  } catch {
    return false
  }
}

/**
 * Synchronous, never throws: `isMirrorIntentActive` and the facts it is made of, for the session
 * report (ATL-2052). An org that cannot be named answers inactive with nothing allowlisted.
 */
export function describeMirrorIntentActivation(orgFirestoreId: string | undefined): MirrorIntentActivation {
  try {
    return journal().describe(orgFirestoreId ?? sessionWriteOrgId())
  } catch {
    return {
      active: false,
      allowlisted: false,
      breakerTripped: null,
      coveredBy: null,
      sbReadersOn: false,
      orgReadsSupabase: false,
    }
  }
}

/**
 * Persist the mirror intent of a Firestore-mode CREATE, before its `batch.commit()`. Never throws and
 * never rejects: a failure is reported and yields a handle that does nothing (today's behaviour).
 * Call `release()` once the mirror call has returned and `abandon()` if the commit or the mirror call
 * threw; see `MirrorIntentHandle`.
 */
export function persistMirrorIntent(spec: MirrorIntentSpec): Promise<MirrorIntentHandle> {
  try {
    return journal().persist(spec)
  } catch {
    return Promise.resolve(NOOP_HANDLE)
  }
}

/** Run a recovery pass for an organization (the session's). Rejects only when the store cannot be read. */
export function recoverMirrorIntents(orgFirestoreId: string): Promise<RecoveryOutcome> {
  return journal().recover(orgFirestoreId)
}

let recoveryStarted = false
const recoveryUnsubscribes: Array<() => void> = []
let lastLiveSuccessPassAt = 0

function runRecoveryPass(orgFirestoreId: string): void {
  void recoverMirrorIntents(orgFirestoreId).catch((error: unknown) => {
    reportOnce(`recovery-failed:${errorMessage(error)}`, 'recovery pass failed — intents kept for the next pass', 'error', {
      kind: 'recovery-failed',
      orgFirestoreId,
      error: errorMessage(error),
    })
  })
}

/**
 * Wire recovery to the moments it can run. Total by construction — called at app boot, it must never
 * be able to prevent one.
 *  - When the session learns which organization it writes through: `dbAtom` (set at sign-in, also
 *    offline) or `organizationAtom` (the v1 outbox's `org-ready` edge, ATL-983). On a relaunch that is
 *    before anything can be sold.
 *  - On every live dual-write success, at most once per `LIVE_SUCCESS_RECOVERY_INTERVAL_MS`: the network
 *    is demonstrably up, which is when a kept (pending/unknown) intent can be answered — sooner than the
 *    `RETRY_MS` tick.
 */
export function startMirrorIntentRecovery(): void {
  if (recoveryStarted) return
  recoveryStarted = true
  try {
    // Lazily required for the same cycle reason as the outbox's auto-drain.
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { getJotaiStore } = require('./types') as {
      getJotaiStore: () => { sub: (atom: unknown, listener: () => void) => () => void }
    }
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { organizationAtom, dbAtom } = require('../AtomsProvider') as { organizationAtom: unknown; dbAtom: unknown }
    const store = getJotaiStore()
    let lastOrgId = sessionWriteOrgId()
    if (lastOrgId) runRecoveryPass(lastOrgId)
    const onIdentity = (): void => {
      const orgId = sessionWriteOrgId()
      // Only a CHANGE of identity is a new opportunity (the org atom is rewritten on every settings edit).
      if (!orgId || orgId === lastOrgId) return
      lastOrgId = orgId
      runRecoveryPass(orgId)
    }
    recoveryUnsubscribes.push(store.sub(organizationAtom, onIdentity), store.sub(dbAtom, onIdentity))
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { addLiveSuccessListener } = require('./dualWriteOutbox') as {
      addLiveSuccessListener: (listener: () => void) => () => void
    }
    recoveryUnsubscribes.push(
      addLiveSuccessListener(() => {
        const orgId = sessionWriteOrgId()
        const at = Date.now()
        if (!orgId || at - lastLiveSuccessPassAt < LIVE_SUCCESS_RECOVERY_INTERVAL_MS) return
        lastLiveSuccessPassAt = at
        runRecoveryPass(orgId)
      }),
    )
  } catch (error) {
    reportOnce('recovery-subscribe-failed', 'recovery could not subscribe to the organization', 'error', {
      kind: 'recovery-subscribe-failed',
      error: errorMessage(error),
    })
  }
}

/** TEST-ONLY: drop the app journal and the recovery subscriptions. */
export function resetMirrorIntentForTests(): void {
  appJournal = null
  if (appRetryTimer) clearTimeout(appRetryTimer)
  appRetryTimer = null
  recoveryStarted = false
  lastLiveSuccessPassAt = 0
  for (const unsubscribe of recoveryUnsubscribes.splice(0)) {
    try {
      unsubscribe()
    } catch {
      // Nothing to do.
    }
  }
  reportedOnce.clear()
}
